import { useState } from 'react';
import i18n from '../../i18n/i18n';
import { useTheme } from '../../hooks/useTheme';
import { useWatch, Controller, SubmitHandler, useForm } from 'react-hook-form';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { getDayjsLocale } from '../../helpers/functions/dateLocales';

// Components
import ContentInfo from '../TitleContentInfo';

// MUI Imports
import { Box, Button, SwipeableDrawer, IconButton, ToggleButtonGroup, ToggleButton, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import SignEvalTextField from '../SignEvalTextField';
import EventIcon from '@mui/icons-material/Event';
import CheckIcon from '@mui/icons-material/Check';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';

// Types
import { SessionFilterType, FilterType, CustomDateFilter } from '../../types/enumSessionFilter';

type Props = {
    currentFilter: FilterType;
    onFilterChange: (filter: FilterType) => void;
};

type FormValues = {
    beginDate: Dayjs;
    endDate: Dayjs;
};

const DrawerFilterSession = ({ currentFilter, onFilterChange }: Props) => {
    const theme = useTheme();
    const [open, setOpen] = useState(false);
    const [selectedFilter, setSelectedFilter] = useState<FilterType>(currentFilter);

    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const {
        handleSubmit,
        control,
        formState: { errors },
        clearErrors
    } = useForm<FormValues>();

    useWatch({ control, name: 'beginDate' });

    const onSubmit: SubmitHandler<FormValues> = (data) => {
        const customDateFilter: CustomDateFilter = {
            type: SessionFilterType.CUSTOM_DATE,
            customStart: data.beginDate.toDate(),
            customEnd: data.endDate.toDate()
        };
        onFilterChange(customDateFilter);
        clearErrors();
        setSelectedFilter(SessionFilterType.CUSTOM_DATE);
        handleClose();
    };

    const handleFilterChange = (_event: React.MouseEvent<HTMLElement>, newFilter: FilterType | null) => {
        if (newFilter !== null) {
            setSelectedFilter(newFilter);
        }
    };

    const renderToggleButton = (value: string, labelKey: string) => (
        <Box key={value} sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
            <ToggleButton
                value={value}
                selected={selectedFilter === value}
                style={{
                    width: '1.3em',
                    height: '1.3em',
                    backgroundColor: selectedFilter === value ? '#5bb3d9' : theme.palette.background.default,
                    border: '2px solid #5bb3d9',
                    borderRadius: '50%',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '0.7em'
                }}
            >
                <CheckIcon
                    sx={{
                        color: selectedFilter === value ? 'white' : theme.palette.background.default,
                        fontSize: '1em'
                    }}
                />
            </ToggleButton>
            {i18n.t(`Sessions.filter.${labelKey}`)}
        </Box>
    );

    const filterOptions = [
        { value: SessionFilterType.ALL, label: 'allSessions' },
        { value: SessionFilterType.CURRENT_WEEK, label: 'currentWeek' },
        { value: SessionFilterType.PREVIOUS_WEEK, label: 'previousWeek' },
        { value: SessionFilterType.NEXT_WEEK, label: 'nextWeek' },
        { value: SessionFilterType.CURRENT_MONTH, label: 'currentMonth' }
    ];

    return (
        <>
            <IconButton onClick={handleOpen} aria-label="filter" sx={{ padding: '0 16px', color: theme.palette.text.custom }}>
                <TuneOutlinedIcon />
            </IconButton>
            <SwipeableDrawer
                anchor="bottom"
                open={open}
                onClose={handleClose}
                onOpen={handleOpen}
                disableSwipeToOpen={true}
                PaperProps={{
                    sx: {
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: theme.palette.background.default,
                        borderRadius: '15px 15px 0 0',
                        paddingBottom: '1em',
                        width: '100%',
                        height: 'auto'
                    }
                }}
            >
                <Box sx={{ width: 45, height: 6, backgroundColor: theme.palette.text.primary, borderRadius: '25px', margin: '1.25em auto 0.5em auto' }} />
                <ContentInfo title={i18n.t('Sessions.filter.title')} />
                <ToggleButtonGroup
                    value={selectedFilter}
                    exclusive
                    onChange={handleFilterChange}
                    sx={{ width: '90%', marginLeft: '1em', marginTop: '1em', display: 'flex', flexDirection: 'column', gap: '0.5em', fontSize: '0.9em' }}
                >
                    {filterOptions.map((option) => renderToggleButton(option.value, option.label))}
                </ToggleButtonGroup>

                <Accordion
                    expanded={selectedFilter === SessionFilterType.CUSTOM_DATE}
                    onChange={() => setSelectedFilter(selectedFilter === SessionFilterType.CUSTOM_DATE ? SessionFilterType.CURRENT_WEEK : SessionFilterType.CUSTOM_DATE)}
                    sx={{
                        width: '100%',
                        boxShadow: 'none',
                        border: 'none',
                        color: theme.palette.text.primary,

                        '&:before': {
                            backgroundColor: theme.palette.background.default
                        }
                    }}
                >
                    <AccordionSummary
                        sx={{
                            display: 'flex',
                            width: '100%',
                            color: theme.palette.text.primary,
                            backgroundColor: theme.palette.background.default,
                            minHeight: '0px',
                            paddingTop: '0.4em',
                            paddingLeft: 0,

                            '&.Mui-expanded': {
                                minHeight: '0px',
                                marginTop: '-1em',
                                padding: '0em'
                            }
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', fontSize: '0.9em', marginLeft: '1em', padding: 0, marginTop: '-0.75em' }}>
                            <ToggleButton
                                value={SessionFilterType.CUSTOM_DATE}
                                selected={selectedFilter === SessionFilterType.CUSTOM_DATE}
                                style={{
                                    width: '1.3em',
                                    height: '1.3em',
                                    backgroundColor: selectedFilter === SessionFilterType.CUSTOM_DATE ? '#5bb3d9' : theme.palette.background.default,
                                    border: '2px solid #5bb3d9',
                                    borderRadius: '50%',
                                    padding: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginRight: '0.7em'
                                }}
                            >
                                <CheckIcon
                                    sx={{
                                        color: selectedFilter === SessionFilterType.CUSTOM_DATE ? 'white' : theme.palette.background.default,
                                        fontSize: '1em'
                                    }}
                                />
                            </ToggleButton>
                            {i18n.t('Sessions.filter.customDate')}
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ backgroundColor: theme.palette.background.default, padding: '0em 1em' }}>
                        <Box display="flex" flexDirection="row" gap="1em">
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={getDayjsLocale(i18n.language)}>
                                <Controller
                                    control={control}
                                    name="beginDate"
                                    defaultValue={dayjs()}
                                    rules={{ required: i18n.t('CreateSession.error.beginDate') }}
                                    render={({ field }) => (
                                        <DatePicker
                                            label={i18n.t('CreateSession.inputs.sessionBeginDate') + ' *'}
                                            value={field.value || null}
                                            onChange={(date) => field.onChange(date)}
                                            slotProps={{
                                                textField: {
                                                    InputProps: {
                                                        endAdornment: <EventIcon />,
                                                        sx: { minHeight: '3em', borderRadius: '10px' }
                                                    },
                                                    inputRef: field.ref,
                                                    error: !!errors.beginDate
                                                },
                                                toolbar: {
                                                    sx: { backgroundColor: theme.palette.text.custom, color: theme.palette.text.secondary }
                                                },
                                                layout: {
                                                    sx: {
                                                        ' .MuiDayCalendar-weekDayLabel': {
                                                            color: theme.palette.text.custom
                                                        },
                                                        '& .MuiPickersDay-root': {
                                                            '&:focus': {
                                                                backgroundColor: theme.palette.text.custom
                                                            },
                                                            '&:active': {
                                                                backgroundColor: theme.palette.text.custom
                                                            },
                                                            '&.Mui-selected': {
                                                                backgroundColor: theme.palette.text.custom
                                                            },
                                                            '&.MuiPickersDay-daySelected': {
                                                                backgroundColor: theme.palette.text.custom
                                                            }
                                                        },
                                                        color: theme.palette.text.primary,
                                                        backgroundColor: theme.palette.background.default
                                                    }
                                                }
                                            }}
                                            localeText={{
                                                toolbarTitle: i18n.t('CreateSession.inputs.sessionBeginDate'),
                                                cancelButtonLabel: i18n.t('CreateSession.button.cancel'),
                                                okButtonLabel: i18n.t('CreateSession.button.ok')
                                            }}
                                            slots={{
                                                textField: SignEvalTextField
                                            }}
                                        />
                                    )}
                                />
                                <Controller
                                    control={control}
                                    name="endDate"
                                    defaultValue={dayjs().add(1, 'day')}
                                    rules={{
                                        required: i18n.t('CreateSession.error.endDate'),
                                        validate: (value) => {
                                            const beginDate = control._getWatch('beginDate');
                                            if (!beginDate) {
                                                return true;
                                            }
                                            if (value && value.isBefore(beginDate)) {
                                                return i18n.t('CreateSession.error.invalidEndDate');
                                            }
                                            return true;
                                        }
                                    }}
                                    render={({ field }) => (
                                        <DatePicker
                                            label={i18n.t('CreateSession.inputs.sessionEndDate') + ' *'}
                                            value={field.value || null}
                                            onChange={(date) => field.onChange(date)}
                                            disablePast
                                            minDate={control._getWatch('beginDate') || null}
                                            slotProps={{
                                                textField: {
                                                    InputProps: {
                                                        endAdornment: <EventIcon />,
                                                        sx: { minHeight: '3em', borderRadius: '10px' }
                                                    },
                                                    inputRef: field.ref,
                                                    error: !!errors.beginDate
                                                },
                                                toolbar: {
                                                    sx: { backgroundColor: theme.palette.text.custom, color: theme.palette.text.secondary }
                                                },
                                                layout: {
                                                    sx: {
                                                        ' .MuiDayCalendar-weekDayLabel': {
                                                            color: theme.palette.text.custom
                                                        },
                                                        '& .MuiPickersDay-today': {
                                                            border: '1px solid ${theme.palette.text.custom}'
                                                        },
                                                        '& .MuiPickersDay-root': {
                                                            '&:focus': {
                                                                backgroundColor: theme.palette.text.custom
                                                            },
                                                            '&:active': {
                                                                backgroundColor: theme.palette.text.custom
                                                            },
                                                            '&.Mui-selected': {
                                                                backgroundColor: theme.palette.text.custom
                                                            },
                                                            '&.MuiPickersDay-daySelected': {
                                                                backgroundColor: theme.palette.text.custom
                                                            }
                                                        },
                                                        color: theme.palette.text.primary,
                                                        backgroundColor: theme.palette.background.default
                                                    }
                                                }
                                            }}
                                            localeText={{
                                                toolbarTitle: i18n.t('CreateSession.inputs.sessionEndDate'),
                                                cancelButtonLabel: i18n.t('CreateSession.button.cancel'),
                                                okButtonLabel: i18n.t('CreateSession.button.ok')
                                            }}
                                            slots={{
                                                textField: SignEvalTextField
                                            }}
                                        />
                                    )}
                                />
                            </LocalizationProvider>
                        </Box>
                    </AccordionDetails>
                </Accordion>

                <Box
                    sx={{
                        width: '92.5%',
                        margin: '0.5em auto 0em auto',
                        paddingTop: '1em',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        fontSize: '0.9em',
                        gap: '1em'
                    }}
                >
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            setSelectedFilter(SessionFilterType.CURRENT_WEEK);
                            onFilterChange(SessionFilterType.CURRENT_WEEK);
                            handleClose();
                        }}
                        sx={{
                            width: '100%',
                            height: '2.5em',
                            borderRadius: '10px',
                            fontSize: '0.9rem',
                            fontWeight: '500',
                            color: theme.palette.text.primary,
                            backgroundColor: theme.palette.background.default,
                            border: `1px solid ${theme.palette.text.primary}`,
                            boxShadow: 'none',
                            textTransform: 'none'
                        }}
                    >
                        {i18n.t('Sessions.filter.actions.reset')}
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            if (selectedFilter === SessionFilterType.CUSTOM_DATE) {
                                handleSubmit(onSubmit)();
                            } else {
                                onFilterChange(selectedFilter);
                                handleClose();
                            }
                        }}
                        sx={{
                            width: '100%',
                            height: '2.5em',
                            borderRadius: '10px',
                            fontSize: '0.9rem',
                            fontWeight: '500',
                            color: theme.palette.text.secondary,
                            backgroundColor: theme.palette.text.custom,
                            boxShadow: 'none',
                            textTransform: 'none'
                        }}
                    >
                        {i18n.t('Sessions.filter.actions.apply')}
                    </Button>
                </Box>
            </SwipeableDrawer>
        </>
    );
};

export default DrawerFilterSession;
